import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useModal, useRequest } from '@stores';
import SessionExpirationModal from '@pages/common/modals/SessionExpiration';
import { useInactivityWarning } from './hooks/useInactivityWarning';
import { saveRequest } from '@forms/input-forms/helpers';
import { useNonPcnUpdateMutation } from '../non-pcn/hooks/useNonPcnUpdateMutation';
import { useFileStorageUploadMutation } from '../file-storage/hooks/useFileStorageUploadMutation';

const TOTAL_TIMEOUT_MS = 15 * 60 * 1000; // Time session expires (15 minutes)
const WARNING_MS = 5 * 60 * 1000; // The warning modal will show when we are this amount of time from the TOTAL_TIMEOUT_MS (5 minutes)

const AuthSessionWatcher = () => {
  const auth = useAuth();
  const { selectedRequestId } = useRequest();
  const { openSecondaryModal, closeSecondaryModal } = useModal();

  const reqTypeId = Number(selectedRequestId);

  const { mutateAsync: updateNonPcn } = useNonPcnUpdateMutation();
  const { mutateAsync: uploadRequestFiles } = useFileStorageUploadMutation();

  // @TODO: Add the rest of the request types as update logic gets migrated to React-Query/Zustand
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateFnMapping: { [key: number]: (data: any) => void } = useMemo(
    () => ({
      11: updateNonPcn,
    }),
    [updateNonPcn]
  );

  const handleWarning = useCallback(() => {
    if (!auth.isAuthenticated) return; // Only show modal if logged in
    const expiresAt = Date.now() + WARNING_MS;
    openSecondaryModal(SessionExpirationModal, {
      expiresAt,
      onContinue: () => {
        closeSecondaryModal();
        window.dispatchEvent(new Event('scroll'));
      },
      onLogout: () => {
        //Save progress if working on an active form:
        const isFormsRoute = window.location.pathname.includes('/forms');
        isFormsRoute && saveRequest(true, uploadRequestFiles, updateFnMapping[reqTypeId]);
        closeSecondaryModal();
        auth.signoutRedirect();
      },
    });
  }, [auth, closeSecondaryModal, openSecondaryModal, uploadRequestFiles, reqTypeId, updateFnMapping]);

  const handleTimeout = useCallback(() => {
    if (!auth.isAuthenticated) return;
    closeSecondaryModal();
    auth.signoutRedirect();
  }, [auth, closeSecondaryModal]);

  useInactivityWarning({
    totalTimeoutMs: TOTAL_TIMEOUT_MS,
    warningMs: WARNING_MS,
    onWarning: handleWarning,
    onTimeout: handleTimeout,
  });

  return null;
};

export default AuthSessionWatcher;
